import type { PageView, Product } from "@telenornorgeinternal/tealium-analytics";
import { ProductCategory } from "~/components/ecommerce/types/ProductCategory";
import { ProductSubCategory } from "~/components/ecommerce/types/ProductSubCategory";

export const useOrderTracking = () => {
	const { getPayNowPrices } = usePayNowPrices();

	const mapProductTealium = (mapItem): Product => {
		const { price, nonMemberPrice, beforePrice } = getPayNowPrices(mapItem, { ignoreDownPayment: true });

		const discounts: Array<{ amount: number; type: string }> = [];
		if (beforePrice) {
			const amount = beforePrice - price;
			const type = nonMemberPrice !== price ? "Member price" : "Sale";
			discounts.push({ amount, type });
		}

		const details = {
			brand: mapItem.metadata?.brand,
			orderType: getOrderType(mapItem), // "New number", "Keep number", or "Port number"
			discounts: discounts,
			size: mapItem.metadata?.memory,
			color: mapItem.metadata?.color,
			condition: mapItem.metadata?.condition,
		};
		let categoryName = mapItem.metadata?.category || mapItem.type;
		let subCategoryName = mapItem.metadata?.subCategory || mapSubcategory(categoryName, mapItem.metadata?.categories);

		// Special case: If the main category is "God Som Ny"
		if (categoryName?.toLowerCase() == "god som ny") {
			categoryName = ProductCategory.PHONE;
			subCategoryName = ProductSubCategory.GOD_SOM_NY;
		}
		return {
			productArea: "E-commerce",
			categoryName,
			subCategoryName,
			id: mapItem.data?.id || mapItem.metadata?.id,
			name: mapItem.metadata?.cleanName || mapItem.metadata?.name,
			quantity: 1,
			price,
			details,
		};
	};

	const mapSubcategory = (categoryName: string, categories?: any[]): string => {
		if (categories && categories.length > 1) {
			const subCategory = categories.find((cat) => cat.name !== categoryName);
			if (subCategory?.name) {
				return subCategory.name;
			}
		}
		const upperSubcategoryName = categoryName.toUpperCase();
		if (upperSubcategoryName in ProductSubCategory) {
			return ProductSubCategory[upperSubcategoryName as keyof typeof ProductSubCategory];
		}
		return ProductSubCategory.OTHER;
	};

	const mapProducts = (terminals): Product[] => {
		const products: Product[] = [];
		for (const terminal of terminals) {
			products.push(mapProductTealium(terminal));
			for (const subItem of terminal?.subItems || []) {
				products.push(mapProductTealium(subItem));
			}
		}
		return products;
	};

	const getOrderType = (mapItem) => {
		const msisdnAction = mapItem?.metadata?.msisdnDetails?.action;
		const accountId = mapItem?.metadata?.msisdnDetails?.accountId;
		switch (msisdnAction) {
			case "NEW_NUMBER":
				return "New number";
			case "KEEP_NUMBER":
				return accountId ? "Keep number" : "Port number";
			default:
				return undefined;
		}
	};

	const orderConfirmationTealium = (orderId, terminals) => {
		return {
			deliveryMethod: terminals[0]?.metadata?.deliveryDetails?.deliveryService,
			orderId: orderId || undefined,
			paymentType: terminals[0]?.metadata?.paymentType,
			products: mapProducts(terminals),
		};
	};

	const getSubCategory = (categories) => {
		if (!categories || categories.length < 2) return null;

		const subCategory = categories.find(
			(category) => category.parentId === categories.find((cat) => cat.id === category.parentId)?.id,
		);

		return subCategory?.name;
	};

	const mapToTrackingProduct = (hardwareProduct: any): Product => ({
		name: hardwareProduct.cleanName,
		id: hardwareProduct.id,
		categoryName: hardwareProduct.categoryName,
		price: hardwareProduct.priceWithVat,
		details: {
			color: hardwareProduct.color,
			condition: hardwareProduct.condition,
			size: hardwareProduct.memory, //size is for storage size
		},
		quantity: 1,
		subCategoryName: getSubCategory(hardwareProduct.categories),
		productArea: hardwareProduct.productArea,
	});

	const trackReserveAndCollectOrder = (hardwareProduct: any, context: string) => {
		const product: Product = mapToTrackingProduct(hardwareProduct);
		const trackingProducts: Product[] = [];
		trackingProducts.push(product);
		const pageView: PageView = { name: context };
		useAnalytics().order.reserve({
			products: trackingProducts,
			view: pageView,
		});
	};

	return {
		mapProducts,
		mapProductTealium,
		orderConfirmationTealium,
		trackReserveAndCollectOrder,
	};
};
